import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { AUTH_CONFIG } from '../auth/config.js';
import { buildAuthorizeUrl } from '../auth/pkce.js';
import { setTokenHandlers } from '../api/api.js';
import { getMe } from '../api/users.js';

const UserContext = createContext({
	user: null,
	accessToken: null,
	signOut: () => {},
	loading: true,
	setTokens: () => {},
});

function decodeJwtPayload(token) {
	try {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		);
		return JSON.parse(jsonPayload);
	} catch (e) {
		console.error('Failed to decode JWT payload:', e);
		return null;
	}
}

function buildUserFromJwt(payload) {
	if (!payload) return null;
	return {
		id: payload.sub,
		email: payload.email || '',
		role: payload.role || 'authenticated',
		user_metadata: {
			full_name: payload.name || payload.preferred_username || '',
			user_name: payload.preferred_username || '',
			name: payload.name || payload.preferred_username || '',
			avatar_url: payload.picture || null,
		},
	};
}

async function refreshAccessToken(refreshToken) {
	const body = new URLSearchParams({
		grant_type: 'refresh_token',
		refresh_token: refreshToken,
		client_id: AUTH_CONFIG.clientId,
	});

	const response = await fetch(`${AUTH_CONFIG.authServerUrl}${AUTH_CONFIG.endpoints.token}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body,
	});

	if (!response.ok) {
		throw new Error('Token refresh failed');
	}

	return response.json();
}

export const UserProvider = ({ children }) => {
	const [accessToken, setAccessToken] = useState(null);
	const [refreshToken, setRefreshToken] = useState(null);
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const refreshTimerRef = useRef(null);
	const accessTokenRef = useRef(null);
	const refreshTokenRef = useRef(null);

	const clearRefreshTimer = useCallback(() => {
		if (refreshTimerRef.current) {
			clearTimeout(refreshTimerRef.current);
			refreshTimerRef.current = null;
		}
	}, []);

	const scheduleRefresh = useCallback(
		(token, currentRefreshToken) => {
			clearRefreshTimer();
			const payload = decodeJwtPayload(token);
			if (!payload?.exp) return;

			const expiresAt = payload.exp * 1000;
			const now = Date.now();
			// Refresh 60 seconds before expiry, minimum 5 seconds from now
			const refreshIn = Math.max(expiresAt - now - 60_000, 5_000);

			refreshTimerRef.current = setTimeout(async () => {
				try {
					const tokens = await refreshAccessToken(currentRefreshToken);
					setAccessToken(tokens.access_token);
					if (tokens.refresh_token) {
						setRefreshToken(tokens.refresh_token);
						sessionStorage.setItem('vizops_refresh_token', tokens.refresh_token);
					}
					const newPayload = decodeJwtPayload(tokens.access_token);
					setUser(buildUserFromJwt(newPayload));
					scheduleRefresh(tokens.access_token, tokens.refresh_token || currentRefreshToken);
				} catch {
					// Refresh failed -- user session expired, redirect to login
					setAccessToken(null);
					setRefreshToken(null);
					setUser(null);
					sessionStorage.removeItem('vizops_refresh_token');
					const loginUrl = await buildAuthorizeUrl();
					window.location.href = loginUrl;
				}
			}, refreshIn);
		},
		[clearRefreshTimer]
	);

	const ensureUserProfile = useCallback(() => {
		getMe().catch(() => {});
	}, []);

	const setTokens = useCallback(
		(newAccessToken, newRefreshToken) => {
			setAccessToken(newAccessToken);
			setRefreshToken(newRefreshToken || null);
			// Update ref synchronously so axios interceptor has the token immediately
			accessTokenRef.current = newAccessToken;
			refreshTokenRef.current = newRefreshToken || null;
			if (newRefreshToken) {
				sessionStorage.setItem('vizops_refresh_token', newRefreshToken);
			}
			const payload = decodeJwtPayload(newAccessToken);
			setUser(buildUserFromJwt(payload));
			setLoading(false);
			ensureUserProfile();
			if (newRefreshToken) {
				scheduleRefresh(newAccessToken, newRefreshToken);
			}
		},
		[scheduleRefresh, ensureUserProfile]
	);

	const signOut = useCallback(() => {
		clearRefreshTimer();
		setAccessToken(null);
		setRefreshToken(null);
		setUser(null);
		sessionStorage.removeItem('vizops_refresh_token');
		// Redirect to auth-service logout to invalidate server session,
		// then Spring redirects to /login after logout
		window.location.href = `${AUTH_CONFIG.authServerUrl}/logout`;
	}, [clearRefreshTimer]);

	// Keep refs in sync with state for axios interceptor access
	useEffect(() => {
		accessTokenRef.current = accessToken;
	}, [accessToken]);

	useEffect(() => {
		refreshTokenRef.current = refreshToken;
	}, [refreshToken]);

	// Wire token handlers into axios interceptor module
	useEffect(() => {
		setTokenHandlers(
			() => accessTokenRef.current,
			async () => {
				const currentRefresh = refreshTokenRef.current;
				if (!currentRefresh) return null;
				try {
					const tokens = await refreshAccessToken(currentRefresh);
					setAccessToken(tokens.access_token);
					if (tokens.refresh_token) {
						setRefreshToken(tokens.refresh_token);
						sessionStorage.setItem('vizops_refresh_token', tokens.refresh_token);
					}
					const newPayload = decodeJwtPayload(tokens.access_token);
					setUser(buildUserFromJwt(newPayload));
					if (tokens.refresh_token || currentRefresh) {
						scheduleRefresh(tokens.access_token, tokens.refresh_token || currentRefresh);
					}
					return tokens.access_token;
				} catch {
					setAccessToken(null);
					setRefreshToken(null);
					setUser(null);
					sessionStorage.removeItem('vizops_refresh_token');
					return null;
				}
			},
		async () => {
			const loginUrl = await buildAuthorizeUrl();
			window.location.href = loginUrl;
		}
		);
	}, [scheduleRefresh]);

	// On mount: try silent refresh from sessionStorage
	useEffect(() => {
		const storedRefreshToken = sessionStorage.getItem('vizops_refresh_token');
		if (storedRefreshToken) {
			refreshAccessToken(storedRefreshToken)
				.then((tokens) => {
					setAccessToken(tokens.access_token);
					setRefreshToken(tokens.refresh_token || storedRefreshToken);
					// Update refs synchronously for axios interceptor
					accessTokenRef.current = tokens.access_token;
					refreshTokenRef.current = tokens.refresh_token || storedRefreshToken;
					if (tokens.refresh_token) {
						sessionStorage.setItem('vizops_refresh_token', tokens.refresh_token);
					}
					const payload = decodeJwtPayload(tokens.access_token);
					setUser(buildUserFromJwt(payload));
					setLoading(false);
					ensureUserProfile();
					scheduleRefresh(tokens.access_token, tokens.refresh_token || storedRefreshToken);
				})
				.catch(() => {
					sessionStorage.removeItem('vizops_refresh_token');
					setLoading(false);
				});
		} else {
			setLoading(false);
		}

		return () => clearRefreshTimer();
	}, [clearRefreshTimer, scheduleRefresh]);

	return (
		<UserContext.Provider value={{ user, accessToken, signOut, loading, setTokens }}>
			{children}
		</UserContext.Provider>
	);
};

UserProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useUser = () => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUser must be used within a UserProvider');
	}
	return context;
};
